import { Tag } from 'antd';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { isHasAccess } from 'features/validateAccess';
import { AccountDTO } from 'entities/account';
import { ReactComponent as TimesheetsIcon } from 'shared/assets/icons/dynamicColor/corner_down_right_arrow_icon.svg';
import { pathKeys } from 'shared/lib/react-router';
import { Avatar } from 'shared/ui/Avatar';
import { LinkButton } from 'shared/ui/LinkButton';
import { ProtectedLink } from 'shared/ui/ProtectedLink';

import { getAccountViewTimesheetPermission } from '../../AccountList.permissions';
import { formatAccountStatus } from '../../lib/formatAccountStatus';

import styles from './AccountItem.module.scss';

export interface AccountItemProps {
  accountId: AccountDTO['id'];
  name: AccountDTO['name'];
  projectsCount: number;
  isActive: AccountDTO['active'];
  logoUrl: AccountDTO['logoUrl'];
  hideEditButton: boolean;
  disableLink: boolean;
  archived: boolean;
}

const AccountItem: React.FC<AccountItemProps> = ({
  accountId,
  name,
  logoUrl,
  disableLink,
  isActive,
  projectsCount,
  hideEditButton,
  archived,
}) => {
  const { t } = useTranslation('Account');
  const isAllowedToViewTimesheets = isHasAccess(getAccountViewTimesheetPermission(accountId));
  return (
    <li className={classNames(styles.accountItem, { [styles.muted]: archived || !isActive })}>
      <div className={styles.accountInfo}>
        <div className={styles.accountMainInfo}>
          <div>
            <Avatar size={32} text={name} picture={logoUrl} />
          </div>
          <div className={styles.nameWrapper}>
            <ProtectedLink
              hasAccess={!disableLink}
              className={classNames(styles.name, {
                [styles.disabled]: disableLink,
              })}
              to={pathKeys.project.root({ accountId })}
            >
              {name}
            </ProtectedLink>
          </div>
        </div>
        <div className={styles.statusBadge}>
          <span className={styles.statusLabel}>{t('AccountList.status')}:</span>
          <Tag
            className={classNames({
              [styles.archived]: archived,
              [styles.active]: !archived && isActive,
              [styles.nonActive]: !archived && !isActive,
            })}
          >
            {formatAccountStatus(isActive, archived, t)}
          </Tag>
        </div>
        <div className={classNames(styles.projectsCounter, { [styles.archived]: archived })}>
          <span className={styles.projectsCounterLabel}>{t('AccountList.projects')}:</span>
          <span>{projectsCount}</span>
        </div>
      </div>
      <div className={styles.actionButtons}>
        {isAllowedToViewTimesheets ? (
          <LinkButton
            buttonProps={{
              icon: <TimesheetsIcon />,
              className: styles.timesheetsButton,
              disabled: archived,
            }}
            linkProps={{ to: `${pathKeys.timesheets()}?accountId=${accountId}` }}
          >
            {t('AccountItem.timesheetsBtn.text')}
          </LinkButton>
        ) : null}
        {!hideEditButton && (
          <LinkButton linkProps={{ to: pathKeys.account.update({ accountId }) }} buttonProps={{ disabled: archived }}>
            {t('Shared:editBtn.text')}
          </LinkButton>
        )}
      </div>
    </li>
  );
};

export default AccountItem;
